import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { usePortalView, useSubmitOffer } from '@/api/sourcing/sourcing.hooks';
import type { SubmitOfferRequest } from '@/api/sourcing/sourcing.types';
import { DsButton, DsTextarea } from '@/modules/internal-v2/ds';
import '@/modules/internal-v2/ds/styles/index.css';
import './sourcing-portal.css';

interface OfferDraft {
  unitPrice: string;
  quantityAvailable: string;
  deliveryDate: string;
  deviatingPartNo: string;
  deviatingEan: string;
  note: string;
}

const EMPTY: OfferDraft = {
  unitPrice: '',
  quantityAvailable: '',
  deliveryDate: '',
  deviatingPartNo: '',
  deviatingEan: '',
  note: '',
};

const fmtDeadline = (iso: string | null): string => {
  if (!iso) return 'keine Frist';
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(new Date(iso));
};

export function SourcingPortalPage() {
  const { token } = useParams<{ token: string }>();
  const { data, isPending, isError } = usePortalView(token);
  const submit = useSubmitOffer(token ?? '');
  const { enqueueSnackbar } = useSnackbar();
  const [drafts, setDrafts] = useState<Record<string, OfferDraft>>({});
  const [message, setMessage] = useState<string | null>(null);

  const initialDrafts = useMemo(() => {
    if (!data) return {};
    const map: Record<string, OfferDraft> = {};
    for (const line of data.lines) {
      const offer = data.offers.find((o) => o.tenderLineId === line.id);
      map[line.id] = offer
        ? {
            unitPrice: String(offer.unitPrice),
            quantityAvailable: String(offer.quantityAvailable),
            deliveryDate: offer.deliveryDate,
            deviatingPartNo: offer.deviatingPartNo ?? '',
            deviatingEan: offer.deviatingEan ?? '',
            note: offer.note ?? '',
          }
        : { ...EMPTY };
    }
    return map;
  }, [data]);

  const effective = (lineId: string): OfferDraft =>
    drafts[lineId] ?? initialDrafts[lineId] ?? EMPTY;

  const set = (lineId: string, patch: Partial<OfferDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [lineId]: { ...effective(lineId), ...patch },
    }));
  };

  if (isPending) {
    return (
      <div data-ds="eos" className="sp-shell">
        <div className="sp-card sp-state">Lädt…</div>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div data-ds="eos" className="sp-shell">
        <div className="sp-card sp-state">
          Dieser Zugangslink ist ungültig oder abgelaufen.
        </div>
      </div>
    );
  }

  const { tender, lines, supplierName, contactName, acceptsOffers } = data;

  const handleSubmit = () => {
    const offers: SubmitOfferRequest['offers'] = [];
    for (const line of lines) {
      const d = effective(line.id);
      const filled = [d.unitPrice, d.quantityAvailable, d.deliveryDate].filter(
        (v) => v.trim() !== '',
      ).length;
      if (filled === 0) continue;
      if (filled < 3) {
        enqueueSnackbar(
          `${line.itemName}: Preis, Menge und Lieferdatum sind alle erforderlich.`,
          { variant: 'error' },
        );
        return;
      }
      const price = Number(d.unitPrice.replace(',', '.'));
      const qty = Number(d.quantityAvailable);
      if (!Number.isFinite(price) || price < 0) {
        enqueueSnackbar(`${line.itemName}: ungültiger Preis.`, {
          variant: 'error',
        });
        return;
      }
      if (!Number.isInteger(qty) || qty < 0) {
        enqueueSnackbar(`${line.itemName}: ungültige Menge.`, {
          variant: 'error',
        });
        return;
      }
      offers.push({
        tenderLineId: line.id,
        unitPrice: Math.round(price * 100) / 100,
        quantityAvailable: qty,
        deliveryDate: new Date(d.deliveryDate).toISOString(),
        deviatingPartNo: d.deviatingPartNo.trim() || undefined,
        deviatingEan: d.deviatingEan.trim() || undefined,
        note: d.note.trim() || undefined,
      });
    }
    if (offers.length === 0) {
      enqueueSnackbar('Bitte mindestens eine Position ausfüllen.', {
        variant: 'warning',
      });
      return;
    }
    const effectiveMessage = (message ?? data.message ?? '').trim();
    submit.mutate(
      { offers, message: effectiveMessage },
      {
        onSuccess: (res) => {
          setDrafts({});
          enqueueSnackbar(
            `Vielen Dank — ${res.accepted} Position(en) übermittelt.`,
            { variant: 'success' },
          );
        },
        onError: () =>
          enqueueSnackbar('Übermittlung fehlgeschlagen.', {
            variant: 'error',
          }),
      },
    );
  };

  return (
    <div data-ds="eos" className="sp-shell">
      <div className="sp-card">
        <header className="sp-head">
          <div className="sp-brand">eLink Distribution AG</div>
          <h1 className="sp-title">
            Ausschreibung {tender.reference}: {tender.title}
          </h1>
          <p className="sp-recipient">
            {contactName ? `${contactName} · ` : ''}
            {supplierName}
          </p>
          {tender.description && (
            <p className="sp-description">{tender.description}</p>
          )}
          <p className="sp-deadline">
            <strong>Angebotsfrist:</strong> {fmtDeadline(tender.deadlineAt)}
          </p>
        </header>

        {!acceptsOffers && (
          <div className="ds-alert warn sp-alert">
            Für diese Ausschreibung können derzeit keine Angebote abgegeben
            werden.
          </div>
        )}
        {data.respondedAt && acceptsOffers && (
          <div className="ds-alert info sp-alert">
            Ihr Angebot ist bei uns eingegangen. Sie können es bis zum
            Fristende weiter anpassen.
          </div>
        )}

        <div className="sp-table-wrap">
          <table className="ds-table sp-table">
            <thead>
              <tr>
                <th>Artikel</th>
                <th className="num">Gesucht</th>
                <th>Preis (EUR)</th>
                <th>Verfügbar</th>
                <th>Lieferdatum</th>
                <th>Abw. ArtNr.</th>
                <th>Abw. EAN</th>
                <th>Bemerkung</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const d = effective(line.id);
                return (
                  <tr key={line.id}>
                    <td>
                      <span className="sp-line-name">{line.itemName}</span>
                      <span className="sp-line-sub">
                        {line.itemEan ?? ''}
                      </span>
                      {line.note && (
                        <span className="sp-line-note">{line.note}</span>
                      )}
                    </td>
                    <td className="num sp-mono">{line.quantity}</td>
                    <td>
                      <input
                        className="ds-input sp-input"
                        inputMode="decimal"
                        disabled={!acceptsOffers}
                        value={d.unitPrice}
                        onChange={(e) =>
                          set(line.id, { unitPrice: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="ds-input sp-input"
                        type="number"
                        min={0}
                        disabled={!acceptsOffers}
                        value={d.quantityAvailable}
                        onChange={(e) =>
                          set(line.id, {
                            quantityAvailable: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="ds-input sp-input"
                        type="date"
                        disabled={!acceptsOffers}
                        value={d.deliveryDate}
                        onChange={(e) =>
                          set(line.id, { deliveryDate: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="ds-input sp-input"
                        disabled={!acceptsOffers}
                        value={d.deviatingPartNo}
                        onChange={(e) =>
                          set(line.id, { deviatingPartNo: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="ds-input sp-input"
                        disabled={!acceptsOffers}
                        value={d.deviatingEan}
                        onChange={(e) =>
                          set(line.id, { deviatingEan: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="ds-input sp-input"
                        disabled={!acceptsOffers}
                        value={d.note}
                        onChange={(e) =>
                          set(line.id, { note: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="sp-message-block">
          <label className="sp-message-label" htmlFor="sp-message">
            Nachricht an den Einkäufer (optional)
          </label>
          <DsTextarea
            id="sp-message"
            rows={4}
            disabled={!acceptsOffers}
            value={message ?? data.message ?? ''}
            placeholder="z. B. Hinweise zu Mindestabnahmen, Lieferbedingungen oder Alternativen…"
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        {acceptsOffers && (
          <div className="sp-actions">
            <span className="sp-hint">
              Positionen ohne Eingabe werden nicht übermittelt.
            </span>
            <DsButton
              variant="primary"
              disabled={submit.isPending}
              onClick={handleSubmit}
            >
              {submit.isPending ? 'Wird gesendet…' : 'Angebot absenden'}
            </DsButton>
          </div>
        )}
      </div>
    </div>
  );
}
